import { Box, Button, Modal, Tooltip, Typography } from '@mui/material';
import { importCsv } from 'apis/business/agent/worksheet/api';
import { useCsvAppend, useGetColumns } from 'apis/business/agent/worksheet/hook';
import UploadFile from 'components/Common/UploadFile';
import { useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import Select from 'components/Common/Select';
import { useForm } from 'react-hook-form';
import Switch from 'components/Common/Switch';

export default function ImportCsvForm() {
  const navigate = useNavigate();
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  const [selectedFiles, setSelectedFiles] = useState<null | any[]>(null);
  const [csvColumns, setCsvColumns] = useState<string[] | null>(null);
  const [view, setView] = useState<'upload' | 'columns'>('upload');

  const { data: columnsData } = useGetColumns();
  const { mutateAsync: appendCsv } = useCsvAppend();

  const handleFileChange = async (files: File[]) => {
    const data = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      data.push(file);
    }
    setSelectedFiles(data);

    const reader = new FileReader();
    reader.readAsText(files[0]);
  };

  const { control, watch, handleSubmit, setValue } = useForm();

  const onSubmit = async () => {
    const formData = new FormData();
    formData.append('file', selectedFiles?.[0]);
    const response = await importCsv(workspaceId!, agentId!, worksheetId!, formData);
    setCsvColumns(response?.csv_columns);
    if (response?.csv_columns?.length) {
      setView('columns');
    }
  };

  const handleAppendCsv = (data: any) => {
    const updatedData = data?.worksheet?.map((item: any, index: number) => {
      const csvColumnValue = csvColumns?.[index] ?? null;
      if (item.skip_import) {
        return { ...item, column_id: null, csv_column: csvColumnValue };
      } else {
        return {
          ...item,
          column_id: item.column_id === undefined ? null : item.column_id,
          csv_column: csvColumnValue
        };
      }
    });
    const payload = {
      worksheet: updatedData
    };
    appendCsv(payload);
  };
  const [selectedOptions, setSelectedOptions] = useState<{ [key: number]: number | string | null }>({});

  const handleSelectChange = (index: number, value: number | string | null) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [index]: value
    }));
    if (value !== 'create_new') {
      setValue(`worksheet[${index}].column_id`, value);
    }
  };
  // Derive the list of currently selected column IDs
  const selectedColumnIds = Object.values(selectedOptions).filter((id) => id);

  return (
    <Modal
      open={true}
      onClose={() => navigate(-1)}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        {view === 'upload' && (
          <Box>
            <Box
              display='flex'
              justifyContent='space-between'
              borderBottom='1px solid #F3F3F3'
              alignItems='center'
              position='sticky'
              bgcolor='white'
              marginBottom='20px'
              top={0}
              zIndex={10000}
            >
              <Typography id='modal-modal-title' variant='h6' component='h2'>
                Import Csv
              </Typography>
              <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
                <IoClose />
              </Box>
            </Box>
            <UploadFile
              allowedFileTypes={['xlsx', 'csv']}
              selectedFiles={selectedFiles}
              handleChange={handleFileChange}
            />
            <Box display='flex' justifyContent='end' mt={1}>
              <Button type='submit' variant='contained' onClick={() => onSubmit()} disabled={!selectedFiles?.length}>
                Continue
              </Button>
            </Box>
          </Box>
        )}
        {view === 'columns' && (
          <Box component='form' onSubmit={handleSubmit(handleAppendCsv)}>
            <Box
              display='flex'
              justifyContent='space-between'
              borderBottom='1px solid #F3F3F3'
              alignItems='center'
              position='sticky'
              bgcolor='white'
              marginBottom='24px'
              top={0}
              zIndex={10000}
            >
              <Typography variant='h6' component='h2'>
                Map CSV to Grid
              </Typography>
              <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
                <IoClose />
              </Box>
            </Box>
            <Box border='2px solid #F3F3F3' borderRadius='10px' padding='20px'>
              <>
                <Grid container mt={1} spacing={3} alignItems='center'>
                  <Grid size={3}>
                    <Typography fontSize='14px' fontWeight='bold'>
                      CSV COLUMN
                    </Typography>
                  </Grid>
                  <Grid size={3}>
                    <Typography fontSize='14px' fontWeight='bold'>
                      Do Not Import
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography fontSize='14px' fontWeight='bold'>
                      GRID COLUMN
                    </Typography>
                  </Grid>
                </Grid>
                {csvColumns?.map((item, index) => (
                  <Grid container mt={2} spacing={3} key={index} alignItems='center'>
                    <Grid size={3}>
                      <Typography>{item}</Typography>
                    </Grid>
                    <Grid size={3} textAlign='center'>
                      <Tooltip title='Do Not Import'>
                        <Switch
                          name={`worksheet[${index}].skip_import`}
                          control={control}
                          size='small'
                          onToggle={(isChecked) => {
                            if (isChecked) {
                              setSelectedOptions((prev) => ({
                                ...prev,
                                [index]: null
                              }));
                            }
                          }}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid size={6} height={40}>
                      {!watch(`worksheet[${index}].skip_import`) && (
                        <Select
                          name={`worksheet[${index}].column_id`}
                          control={control}
                          label='column'
                          size='small'
                          options={[
                            { label: 'Create New Column', value: 'create_new' },
                            ...(columnsData?.data
                              ?.filter((col) => col.data_type !== 'app_execution')
                              .map((col) => ({
                                label: col.name,
                                value: col.id,
                                disabled: selectedColumnIds.includes(col.id)
                              })) ?? [])
                          ]}
                          onChange={(e) => {
                            const selectedValue = e.target.value === 'create_new' ? null : Number(e.target.value);
                            handleSelectChange(index, selectedValue);
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                ))}
              </>
            </Box>
            <Box display='flex' justifyContent='end' mt={2} gap={1}>
              <Button type='button' variant='outlined' onClick={() => setView('upload')}>
                Back
              </Button>
              <Button type='submit' onSubmit={handleAppendCsv}>
                Submit
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  padding: '16px 24px',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  borderRadius: '8px',
  width: {
    xs: '90%',
    sm: 400,
    md: 700
  }
};
